/**
 * Patient Form Page (Create/Edit) - Enhanced
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  ContactPhone as CrmIcon,
  Phone as PhoneIcon,
  LocalHospital as HospitalIcon,
  Description as DocumentIcon,
  AccountBalance as InsuranceIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetPatientQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  LEAD_SOURCES,
  CONTACT_METHODS,
} from '../../store/api/patientsApi';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// 12-column layout: fields span 12 / 6 / 4 columns depending on screen width
const formGridSx = { display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 };

const PatientFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: patientData, isLoading: isLoadingPatient } = useGetPatientQuery(Number(id!), {
    skip: !isEdit,
  });

  const [createPatient, { isLoading: isCreating }] = useCreatePatientMutation();
  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation();

  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    age: '',
    gender: '',
    nationality: '',
    phone_number: '',
    email: '',
    blood_type: '',
    height: '',
    weight: '',
    national_id: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    special_notes: '',
    health_conditions: '',
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_expiry_date: '',
    lead_source: '',
    lead_source_details: '',
    previous_home_care_experience: '',
    preferred_contact_method: '',
    preferred_contact_time: '',
    contact_notes: '',
    is_active: true,
  });

  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [medicalReports, setMedicalReports] = useState<File | null>(null);
  const [prescriptions, setPrescriptions] = useState<File | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load patient data when editing
  useEffect(() => {
    if (isEdit && patientData?.data) {
      const patient = patientData.data;
      setFormData({
        full_name: patient.full_name || '',
        date_of_birth: patient.date_of_birth || '',
        age: patient.age != null ? String(patient.age) : '',
        gender: patient.gender || '',
        nationality: patient.nationality || '',
        phone_number: patient.phone_number || '',
        email: patient.email || '',
        blood_type: patient.blood_type || '',
        height: patient.height ? String(patient.height) : '',
        weight: patient.weight ? String(patient.weight) : '',
        national_id: patient.national_id || '',
        emergency_contact_name: patient.emergency_contact_name || '',
        emergency_contact_phone: patient.emergency_contact_phone || '',
        special_notes: patient.special_notes || '',
        health_conditions: patient.health_conditions || '',
        insurance_provider: patient.insurance_provider || '',
        insurance_policy_number: patient.insurance_policy_number || '',
        insurance_expiry_date: patient.insurance_expiry_date || '',
        lead_source: patient.lead_source || '',
        lead_source_details: patient.lead_source_details || '',
        previous_home_care_experience: patient.previous_home_care_experience || '',
        preferred_contact_method: patient.preferred_contact_method || '',
        preferred_contact_time: patient.preferred_contact_time || '',
        contact_notes: patient.contact_notes || '',
        is_active: patient.is_active ?? true,
      });
    }
  }, [isEdit, patientData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t('users.validation.fullNameRequired');
    }

    if (formData.date_of_birth && new Date(formData.date_of_birth) > new Date()) {
      newErrors.date_of_birth = t('users.validation.dobFuture');
    }

    if (formData.age && (Number(formData.age) < 0 || Number(formData.age) > 150)) {
      newErrors.age = t('users.validation.ageRange');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = t('users.validation.phoneRequired');
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone_number.replace(/\s/g, ''))) {
      newErrors.phone_number = t('users.validation.phoneInvalid');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('users.validation.emailInvalid');
    }

    if (formData.height && (parseFloat(formData.height) <= 0 || parseFloat(formData.height) > 300)) {
      newErrors.height = t('users.validation.heightRange');
    }

    if (formData.weight && (parseFloat(formData.weight) <= 0 || parseFloat(formData.weight) > 500)) {
      newErrors.weight = t('users.validation.weightRange');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('users.toast.fixErrorsBeforeSubmit'));
      return;
    }

    try {
      const submitData = new FormData();

      // Required fields
      submitData.append('full_name', formData.full_name.trim());
      submitData.append('phone_number', formData.phone_number.trim());

      // Optional fields. When editing, text fields are always sent so clearing one saves.
      const appendText = (key: string, value: string) => {
        const v = value.trim();
        if (v || isEdit) submitData.append(key, v);
      };
      if (formData.date_of_birth) submitData.append('date_of_birth', formData.date_of_birth);
      else if (formData.age) submitData.append('age', formData.age);
      appendText('gender', formData.gender);
      appendText('nationality', formData.nationality);
      appendText('email', formData.email);
      appendText('blood_type', formData.blood_type);
      if (formData.height) submitData.append('height', formData.height);
      if (formData.weight) submitData.append('weight', formData.weight);
      appendText('national_id', formData.national_id);
      appendText('emergency_contact_name', formData.emergency_contact_name);
      appendText('emergency_contact_phone', formData.emergency_contact_phone);
      appendText('special_notes', formData.special_notes);
      appendText('health_conditions', formData.health_conditions);
      appendText('insurance_provider', formData.insurance_provider);
      appendText('insurance_policy_number', formData.insurance_policy_number);
      if (formData.insurance_expiry_date) submitData.append('insurance_expiry_date', formData.insurance_expiry_date);
      appendText('lead_source', formData.lead_source);
      appendText('lead_source_details', formData.lead_source_details);
      appendText('previous_home_care_experience', formData.previous_home_care_experience);
      appendText('preferred_contact_method', formData.preferred_contact_method);
      appendText('preferred_contact_time', formData.preferred_contact_time);
      appendText('contact_notes', formData.contact_notes);
      if (isEdit) submitData.append('is_active', String(formData.is_active));

      // File uploads (only if new files are selected)
      if (idDocument) submitData.append('id_document', idDocument);
      if (medicalReports) submitData.append('medical_reports', medicalReports);
      if (prescriptions) submitData.append('prescriptions', prescriptions);

      if (isEdit) {
        await updatePatient({ id: Number(id!), data: submitData as any }).unwrap();
        toast.success(t('users.toast.updateSuccess'));
      } else {
        await createPatient(submitData as any).unwrap();
        toast.success(t('users.toast.createSuccess'));
      }

      navigate('/users');
    } catch (err: any) {
      const errorMessage = err?.data?.message || (isEdit ? t('users.toast.updateError') : t('users.toast.createError'));
      const errorDetails = err?.data?.errors;

      if (errorDetails) {
        // Set field-specific errors
        const fieldErrors: Record<string, string> = {};
        Object.keys(errorDetails).forEach((key) => {
          if (Array.isArray(errorDetails[key])) {
            fieldErrors[key] = errorDetails[key][0];
          } else {
            fieldErrors[key] = errorDetails[key];
          }
        });
        setErrors(fieldErrors);
        toast.error(t('users.toast.fixErrorsInForm'));
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleFileChange = (setter: (file: File | null) => void, file: File | null) => {
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(t('users.toast.fileTooLarge'));
        return;
      }
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('users.toast.fileInvalidType'));
        return;
      }
    }
    setter(file);
  };

  const getFileName = (file: File | null, existingUrl: string | null | undefined): string => {
    if (file) return file.name;
    if (existingUrl) {
      const parts = existingUrl.split('/');
      return parts[parts.length - 1];
    }
    return t('users.form.noFileSelected');
  };

  if (isEdit && isLoadingPatient) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const patient = patientData?.data;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('users.form.editTitle') : t('users.form.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('users.breadcrumb'), path: '/users' },
          { label: isEdit ? t('users.form.editTitle') : t('users.form.createTitle') },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/users')}
        sx={{ mb: 3 }}
      >
        {t('users.buttons.backToPatients')}
      </Button>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information Section */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.basicInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={formGridSx}>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.fullName')}
                    value={formData.full_name}
                    onChange={(e) => {
                      setFormData({ ...formData, full_name: e.target.value });
                      if (errors.full_name) setErrors({ ...errors, full_name: '' });
                    }}
                    error={!!errors.full_name}
                    helperText={errors.full_name}
                    required
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.dateOfBirth')}
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => {
                      setFormData({ ...formData, date_of_birth: e.target.value });
                      if (errors.date_of_birth) setErrors({ ...errors, date_of_birth: '' });
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.date_of_birth}
                    helperText={errors.date_of_birth}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.age')}
                    type="number"
                    value={formData.age}
                    onChange={(e) => {
                      setFormData({ ...formData, age: e.target.value });
                      if (errors.age) setErrors({ ...errors, age: '' });
                    }}
                    disabled={!!formData.date_of_birth}
                    error={!!errors.age}
                    helperText={errors.age || t('users.form.ageHelp')}
                    inputProps={{ min: 0, max: 150 }}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <FormControl fullWidth error={!!errors.gender}>
                    <InputLabel>{t('users.form.gender')}</InputLabel>
                    <Select
                      value={formData.gender}
                      onChange={(e) => {
                        setFormData({ ...formData, gender: e.target.value });
                        if (errors.gender) setErrors({ ...errors, gender: '' });
                      }}
                      label={t('users.form.gender')}
                    >
                      <MenuItem value="">
                        <em>{t('users.form.notSpecified')}</em>
                      </MenuItem>
                      <MenuItem value="M">{t('users.gender.M')}</MenuItem>
                      <MenuItem value="F">{t('users.gender.F')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {isEdit && (
                  <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                    <TextField
                      fullWidth
                      label={t('users.form.nationalId')}
                      value={formData.national_id}
                      onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                      helperText={t('users.form.nationalIdHelp')}
                    />
                  </Box>
                )}
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.nationality')}
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.heightCm')}
                    type="number"
                    value={formData.height}
                    onChange={(e) => {
                      setFormData({ ...formData, height: e.target.value });
                      if (errors.height) setErrors({ ...errors, height: '' });
                    }}
                    error={!!errors.height}
                    helperText={errors.height || t('users.form.heightHelp')}
                    inputProps={{ min: 1, max: 300, step: 0.1 }}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.weightKg')}
                    type="number"
                    value={formData.weight}
                    onChange={(e) => {
                      setFormData({ ...formData, weight: e.target.value });
                      if (errors.weight) setErrors({ ...errors, weight: '' });
                    }}
                    error={!!errors.weight}
                    helperText={errors.weight || t('users.form.weightHelp')}
                    inputProps={{ min: 1, max: 500, step: 0.1 }}
                  />
                </Box>
                {formData.height && formData.weight && (
                  <>
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Chip
                        label={t('users.form.bmiLabel', {
                          value: (
                            parseFloat(formData.weight) /
                            Math.pow(parseFloat(formData.height) / 100, 2)
                          ).toFixed(1),
                        })}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Contact Information Section */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.contactInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={formGridSx}>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.phoneNumber')}
                    value={formData.phone_number}
                    onChange={(e) => {
                      setFormData({ ...formData, phone_number: e.target.value });
                      if (errors.phone_number) setErrors({ ...errors, phone_number: '' });
                    }}
                    error={!!errors.phone_number}
                    helperText={errors.phone_number || t('users.form.phoneHelp')}
                    required
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.email')}
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (errors.email) setErrors({ ...errors, email: '' });
                    }}
                    error={!!errors.email}
                    helperText={errors.email || (isEdit ? t('users.form.emailHelp') : t('users.form.emailOptionalHelp'))}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.emergencyContactName')}
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    helperText={t('users.form.emergencyContactNameHelp')}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.emergencyContactPhone')}
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    helperText={t('users.form.emergencyContactPhoneHelp')}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Lead & contact preferences */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CrmIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.crmInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={formGridSx}>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('users.form.leadSource')}</InputLabel>
                    <Select
                      value={formData.lead_source}
                      onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
                      label={t('users.form.leadSource')}
                    >
                      <MenuItem value="">
                        <em>{t('users.form.notSpecified')}</em>
                      </MenuItem>
                      {LEAD_SOURCES.map((src) => (
                        <MenuItem key={src} value={src}>
                          {t(`users.leadSources.${src}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.leadSourceDetails')}
                    value={formData.lead_source_details}
                    onChange={(e) => setFormData({ ...formData, lead_source_details: e.target.value })}
                    helperText={t('users.form.leadSourceDetailsHelp')}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('users.form.contactMethod')}</InputLabel>
                    <Select
                      value={formData.preferred_contact_method}
                      onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                      label={t('users.form.contactMethod')}
                    >
                      <MenuItem value="">
                        <em>{t('users.form.notSpecified')}</em>
                      </MenuItem>
                      {CONTACT_METHODS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {t(`users.contactMethods.${m}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <TextField
                    fullWidth
                    label={t('users.form.contactTime')}
                    value={formData.preferred_contact_time}
                    onChange={(e) => setFormData({ ...formData, preferred_contact_time: e.target.value })}
                    helperText={t('users.form.contactTimeHelp')}
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    fullWidth
                    label={t('users.form.contactNotes')}
                    value={formData.contact_notes}
                    onChange={(e) => setFormData({ ...formData, contact_notes: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    fullWidth
                    label={t('users.form.previousExperience')}
                    value={formData.previous_home_care_experience}
                    onChange={(e) => setFormData({ ...formData, previous_home_care_experience: e.target.value })}
                    multiline
                    rows={3}
                    helperText={t('users.form.previousExperienceHelp')}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Medical Information Section (edit only — the create form stays short) */}
          {isEdit && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <HospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('users.sections.medicalInfo')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ width: '100%', mb: 3 }}>
                    <FormControl fullWidth sx={{ width: '100%' }}>
                      <InputLabel id="blood-type-label" shrink sx={{ transform: 'translate(14px, -9px) scale(0.75)' }}>
                        {t('users.form.bloodType')}
                      </InputLabel>
                      <Select
                        labelId="blood-type-label"
                        value={formData.blood_type || ''}
                        onChange={(e) => setFormData({ ...formData, blood_type: e.target.value })}
                        label={t('users.form.bloodType')}
                        displayEmpty
                        inputProps={{
                          'aria-label': t('users.form.bloodType'),
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiSelect-select': {
                            paddingTop: '16.5px',
                            paddingBottom: '16.5px',
                          },
                        }}
                        renderValue={(selected) => {
                          if (!selected || selected === '') {
                            return <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{t('users.form.selectBloodType')}</span>;
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('users.form.none')}</em>
                        </MenuItem>
                        <MenuItem value="A+">A+</MenuItem>
                        <MenuItem value="A-">A-</MenuItem>
                        <MenuItem value="B+">B+</MenuItem>
                        <MenuItem value="B-">B-</MenuItem>
                        <MenuItem value="AB+">AB+</MenuItem>
                        <MenuItem value="AB-">AB-</MenuItem>
                        <MenuItem value="O+">O+</MenuItem>
                        <MenuItem value="O-">O-</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ width: '100%', mb: 3 }}>
                    <TextField
                      fullWidth
                      label={t('users.form.healthConditions')}
                      value={formData.health_conditions}
                      onChange={(e) => setFormData({ ...formData, health_conditions: e.target.value })}
                      multiline
                      rows={5}
                      helperText={t('users.form.healthConditionsHelp')}
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          minHeight: '120px',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      label={t('users.form.specialNotes')}
                      value={formData.special_notes}
                      onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                      multiline
                      rows={5}
                      helperText={t('users.form.specialNotesHelp')}
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          minHeight: '120px',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Insurance Information Section (edit only) */}
          {isEdit && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <InsuranceIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('users.sections.insuranceInfo')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box sx={formGridSx}>
                  <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <TextField
                      fullWidth
                      label={t('users.form.insuranceProvider')}
                      value={formData.insurance_provider}
                      onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
                      helperText={t('users.form.insuranceProviderHelp')}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <TextField
                      fullWidth
                      label={t('users.form.insurancePolicyNumber')}
                      value={formData.insurance_policy_number}
                      onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
                      helperText={t('users.form.insurancePolicyNumberHelp')}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <TextField
                      fullWidth
                      label={t('users.form.insuranceExpiryDate')}
                      type="date"
                      value={formData.insurance_expiry_date}
                      onChange={(e) => setFormData({ ...formData, insurance_expiry_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('users.form.insuranceExpiryDateHelp')}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Medical Documents Section */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <DocumentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.medicalDocuments')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={formGridSx}>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t('users.form.idDocument')}
                    </Typography>
                    {isEdit && patient?.id_document && !idDocument && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={getFileName(null, patient.id_document)}
                          onDelete={() => { }}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('users.form.currentFile')}
                        </Typography>
                      </Box>
                    )}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => document.getElementById('id-document-input')?.click()}
                    >
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        {idDocument ? idDocument.name : t('users.form.clickToUpload')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {t('users.form.fileTypesHint')}
                      </Typography>
                      <input
                        id="id-document-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(setIdDocument, e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                      />
                    </Paper>
                    {idDocument && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={idDocument.name} size="small" />
                        <IconButton
                          size="small"
                          onClick={() => setIdDocument(null)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {isEdit && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t('users.form.leaveEmptyToKeep')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t('users.form.medicalReports')}
                    </Typography>
                    {isEdit && patient?.medical_reports && !medicalReports && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={getFileName(null, patient.medical_reports)}
                          onDelete={() => { }}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('users.form.currentFile')}
                        </Typography>
                      </Box>
                    )}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => document.getElementById('medical-reports-input')?.click()}
                    >
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        {medicalReports ? medicalReports.name : t('users.form.clickToUpload')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {t('users.form.fileTypesHint')}
                      </Typography>
                      <input
                        id="medical-reports-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(setMedicalReports, e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                      />
                    </Paper>
                    {medicalReports && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={medicalReports.name} size="small" />
                        <IconButton
                          size="small"
                          onClick={() => setMedicalReports(null)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {isEdit && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t('users.form.leaveEmptyToKeep')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t('users.form.prescriptions')}
                    </Typography>
                    {isEdit && patient?.prescriptions && !prescriptions && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={getFileName(null, patient.prescriptions)}
                          onDelete={() => { }}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('users.form.currentFile')}
                        </Typography>
                      </Box>
                    )}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => document.getElementById('prescriptions-input')?.click()}
                    >
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        {prescriptions ? prescriptions.name : t('users.form.clickToUpload')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {t('users.form.fileTypesHint')}
                      </Typography>
                      <input
                        id="prescriptions-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(setPrescriptions, e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                      />
                    </Paper>
                    {prescriptions && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={prescriptions.name} size="small" />
                        <IconButton
                          size="small"
                          onClick={() => setPrescriptions(null)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {isEdit && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t('users.form.leaveEmptyToKeep')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Status Section (Edit only) */}
          {isEdit && (
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {t('users.form.activeStatus')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('users.form.activeStatusHelp')}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          )}

          {/* Form Actions */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => navigate('/users')}
                  variant="outlined"
                  size="large"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={!formData.full_name || !formData.phone_number || isCreating || isUpdating}
                >
                  {isCreating || isUpdating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {isEdit ? t('users.buttons.updating') : t('users.buttons.creating')}
                    </>
                  ) : (
                    isEdit ? t('users.buttons.updatePatient') : t('users.buttons.createPatient')
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </form>
    </Box>
  );
};

export default PatientFormPage;
